import React, { useEffect, useState } from 'react';
import { Icon, IconName } from './Icon';
import { statusInfo } from '../../utils/format';

// ─── Status pill ───
export const StatusPill: React.FC<{ status: string | undefined; short?: boolean }> = ({ status, short }) => {
  const it = statusInfo(status);
  return (
    <span className={`pill ${it.pill}`}>
      <span className={`dot ${it.dot}`} />
      {short ? it.shortText : it.text}
    </span>
  );
};

// ─── Toggle ───
export const Toggle: React.FC<{ checked: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label?: string }> = ({
  checked,
  onChange,
  disabled,
  label,
}) => (
  <button
    type="button"
    className="toggle"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => !disabled && onChange && onChange(!checked)}
    disabled={disabled}
  />
);

// ─── Player head (Minecraft face by uuid, CSS fallback) ───
const COLORS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const colorFor = (seed: string | null | undefined): string => {
  if (!seed) return 'g';
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return COLORS[Math.abs(h) % COLORS.length];
};

export const PlayerHead: React.FC<{ uuid?: string | null; size?: number; style?: React.CSSProperties; title?: string }> = ({
  uuid,
  size = 28,
  style = {},
  title,
}) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [uuid]);
  const showEyes = size >= 18;
  const eye = Math.max(2, Math.round(size * 0.12));
  const useImage = !!uuid && !failed;
  return (
    <span className={`head head--${colorFor(uuid)}`} style={{ width: size, height: size, ...style }} title={title}>
      {useImage ? (
        <img
          src={`https://crafthead.net/avatar/${uuid}/${Math.max(16, size * 2)}`}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        showEyes && (
          <>
            <span className="head-eye" style={{ width: eye, height: eye, top: `${size * 0.38}px`, left: `${size * 0.26}px` }} />
            <span className="head-eye" style={{ width: eye, height: eye, top: `${size * 0.38}px`, left: `${size * 0.6}px` }} />
          </>
        )
      )}
    </span>
  );
};

// ─── Minecraft block icons ───
export const BLOCKS: Record<string, { top: string; side: string; label: string; sparkle?: boolean }> = {
  grass: { top: '#7bc35a', side: '#8a6b46', label: 'Grass' },
  dirt: { top: '#9a7350', side: '#7a5a3a', label: 'Dirt' },
  stone: { top: '#a3a3a3', side: '#828282', label: 'Stone' },
  cobblestone: { top: '#9a9a9a', side: '#767676', label: 'Cobblestone' },
  sand: { top: '#ece3b0', side: '#d3c389', label: 'Sand' },
  snow: { top: '#ffffff', side: '#dce6ef', label: 'Snow' },
  oak: { top: '#c19a5b', side: '#a47f44', label: 'Oak' },
  netherrack: { top: '#8a3b34', side: '#6a2922', label: 'Netherrack' },
  diamond: { top: '#7fe6e0', side: '#57bdb8', label: 'Diamond', sparkle: true },
  gold: { top: '#f4d04a', side: '#d4a92f', label: 'Gold', sparkle: true },
  water: { top: '#5b8cff', side: '#3f6ad6', label: 'Water' },
  mossy: { top: '#7a9a6a', side: '#5e7d50', label: 'Mossy' },
};

export const BlockIcon: React.FC<{ block?: string; size?: number; style?: React.CSSProperties }> = ({ block = 'grass', size = 28, style = {} }) => {
  const b = BLOCKS[block] || BLOCKS.grass;
  return (
    <span
      className="blk"
      style={{
        width: size,
        height: size,
        backgroundImage:
          `repeating-linear-gradient(45deg, rgba(0,0,0,.06) 0 2px, transparent 2px 4px),` +
          `linear-gradient(180deg, ${b.top} 0 36%, ${b.side} 36% 100%)`,
        ...style,
      }}
    >
      {b.sparkle && <span className="blk-sparkle" />}
    </span>
  );
};

// ─── Confirm modal ───
export interface ConfirmProps {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const Confirm: React.FC<ConfirmProps> = ({ open, title, body, confirmLabel = 'Confirm', danger, busy, error, onConfirm, onCancel }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);
  if (!open) return null;
  return (
    <div className="modal-stage" onClick={() => !busy && onCancel()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{body}</p>
        {error && <Note tone="danger">{error}</Note>}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={`btn ${danger ? 'btn--danger-solid' : 'btn--primary'}`} onClick={onConfirm} disabled={busy}>
            {busy && <span className="spin" style={{ borderTopColor: '#fff' }} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Note / alert ───
export const Note: React.FC<{ tone?: 'info' | 'danger' | 'ok' | 'warn'; icon?: IconName; children: React.ReactNode; className?: string }> = ({
  tone = 'info',
  icon,
  children,
  className = '',
}) => {
  const iconName: IconName = icon || (tone === 'danger' ? 'warn' : tone === 'ok' ? 'check' : tone === 'warn' ? 'warn' : 'shield');
  return (
    <div className={`note note--inline ${tone !== 'info' ? `note--${tone}` : ''} ${className}`}>
      <Icon name={iconName} size={15} />
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  );
};

// ─── Empty state ───
export const Empty: React.FC<{ icon: IconName; title: string; children?: React.ReactNode; action?: React.ReactNode; big?: boolean }> = ({
  icon,
  title,
  children,
  action,
  big,
}) => (
  <div className="empty">
    <div className="empty-art" style={big ? { width: 110, height: 110 } : undefined}>
      <Icon name={icon} size={big ? 40 : 30} />
    </div>
    <h3>{title}</h3>
    {children && <p>{children}</p>}
    {action}
  </div>
);

// ─── Loading ───
export const Loading: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <div className="center">
    <span className="spin" /> {label}
  </div>
);

// ─── Section (ledger layout) ───
export const Section: React.FC<{ title: string; blurb?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; wide?: boolean }> = ({
  title,
  blurb,
  actions,
  children,
  wide,
}) => (
  <section className="sec" style={wide ? { gridTemplateColumns: '1fr' } : undefined}>
    <div className="sec-head" style={wide ? { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' } : undefined}>
      <div>
        <h2>{title}</h2>
        {blurb && <p style={wide ? { maxWidth: 'none' } : undefined}>{blurb}</p>}
      </div>
      {actions && <div className={wide ? '' : 'sec-acts'} style={wide ? { display: 'flex', gap: 8, flexWrap: 'wrap' } : undefined}>{actions}</div>}
    </div>
    <div className="sec-body">{children}</div>
  </section>
);
